import type { UserRole } from "@src/context/ApplicationContext";
import moment from "moment-timezone";

export const getEighteenYearsAgeDate = () => {
  const today = new Date();
  const eighteenYearsAgo = new Date(today);
  eighteenYearsAgo.setFullYear(today.getFullYear() - 18); // Calculate the date of 18 years of age
  return eighteenYearsAgo;
};

export const getTwelveYearsAgeDate = () => {
  const today = new Date();
  const twelveYearsAgo = new Date(today);
  twelveYearsAgo.setFullYear(today.getFullYear() - 12); // Calculate the date of 12 years of age
  return twelveYearsAgo;
};

// Helper function to get ordinal suffix (st, nd, rd, th)
export const getOrdinalSuffix = (day: number) => {
  if (day >= 11 && day <= 13) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

// Validate date of birth format and return the date if valid
export const validateDateOfBirth = (value: string | null | undefined): Date | undefined => {
  if (!value) {
    return undefined;
  }

  const dob = new Date(value);
  if (Number.isNaN(dob.getTime())) {
    return undefined;
  }

  return dob;
};

export const ISO_DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const US_DATE_ONLY_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;

const createValidatedLocalDate = (
  year: number,
  monthIndex: number,
  day: number,
): Date | undefined => {
  const localDate = new Date(year, monthIndex, day);

  if (
    localDate.getFullYear() !== year ||
    localDate.getMonth() !== monthIndex ||
    localDate.getDate() !== day
  ) {
    return undefined;
  }

  return localDate;
};

export const parseDateForCalendarComparison = (value: string | Date): Date | undefined => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }

  const isoDateOnlyMatch = ISO_DATE_ONLY_PATTERN.exec(value);
  if (isoDateOnlyMatch) {
    const year = Number(isoDateOnlyMatch[1]);
    const monthIndex = Number(isoDateOnlyMatch[2]) - 1;
    const day = Number(isoDateOnlyMatch[3]);
    return createValidatedLocalDate(year, monthIndex, day);
  }

  const usDateOnlyMatch = US_DATE_ONLY_PATTERN.exec(value);
  if (usDateOnlyMatch) {
    const monthIndex = Number(usDateOnlyMatch[1]) - 1;
    const day = Number(usDateOnlyMatch[2]);
    const year = Number(usDateOnlyMatch[3]);
    return createValidatedLocalDate(year, monthIndex, day);
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? undefined : parsedDate;
};

/**
 * Normalizes a date string to `MM/DD/YYYY`, the format expected by the availability APIs
 * and the date picker's masked input. Accepts `MM/DD/YYYY`, `YYYY-MM-DD`, or ISO 8601
 * input; returns the original string unchanged when it cannot be parsed.
 */
export const normalizeToApiDate = (date: string): string => {
  // Preserve the source calendar date for timestamps with an offset (including `Z`).
  // Converting such a timestamp to the browser timezone before formatting can turn
  // midnight UTC on August 5 into August 4 for users west of UTC.
  const parsed = moment.parseZone(date, ["MM/DD/YYYY", "YYYY-MM-DD", moment.ISO_8601], true);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY") : date;
};

/**
 * Combines an appointment date and time into an ISO 8601 wall-clock datetime
 * string (`YYYY-MM-DDTHH:mm:ss`), the format expected by the patient-data API.
 *
 * Accepts the appointment date in `MM/DD/YYYY`, `YYYY-MM-DD`, or ISO 8601 form and
 * the appointment time in 12-hour (`h:mm A`, e.g. "10:00 AM") or 24-hour
 * (`H:mm`) form. The output preserves the local wall-clock value without applying
 * any timezone conversion. Returns `null` when either input is missing or invalid.
 */
export const toAppointmentIsoDateTime = (
  apptDate?: string | null,
  apptTime?: string | null,
): string | null => {
  if (!apptDate || !apptTime) {
    return null;
  }

  const parsed = moment(
    `${apptDate.trim()} ${apptTime.trim()}`,
    [
      "MM/DD/YYYY h:mm A",
      "YYYY-MM-DD h:mm A",
      "MM/DD/YYYY H:mm",
      "YYYY-MM-DD H:mm",
      "MM/DD/YYYY HH:mm",
      "YYYY-MM-DD HH:mm",
    ],
    true,
  );

  return parsed.isValid() ? parsed.format("YYYY-MM-DDTHH:mm:ss") : null;
};

/**
 * Returns true when the provided date is strictly before today.
 * Today and future dates return false.
 */
export const isDateBeforeToday = (value: string | Date | null | undefined): boolean => {
  if (!value) {
    return false;
  }

  const parsedDate = parseDateForCalendarComparison(value);
  if (!parsedDate) {
    return false;
  }

  const dateOnly = new Date(parsedDate);
  dateOnly.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return dateOnly < today;
};

/**
 * Calculate age in years from date of birth
 * @param dob - Date of birth string in any valid date format
 * @returns Age in years
 */
export const calculateAgeInYears = (dob: string): number => {
  // Parse as a local calendar date so date-only strings (YYYY-MM-DD) are not shifted
  // by a day via UTC parsing, which would produce off-by-one ages around birthdays.
  const birthDate = parseDateForCalendarComparison(dob) ?? new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
};

/**
 * Get age range category based on age in years
 * Determines which age bracket a person falls into based on their age
 *
 * @param age - Age in years
 * @returns Age range label for all age groups, or null for invalid ages
 *
 * @example
 * ```typescript
 * getAgeRange(3)  // Returns "Early Childhood (0-5)"
 * getAgeRange(8)  // Returns "Children (6-11)"
 * getAgeRange(15) // Returns "Adolescents (12-17)"
 * getAgeRange(25) // Returns "Young Adult (18-29)"
 * getAgeRange(45) // Returns "Adult (30-64)"
 * getAgeRange(70) // Returns "Older Adult (65+)"
 * ```
 */
export const getAgeRange = (age: number): string | null => {
  if (age >= 0 && age <= 5) return "Early Childhood (0-5)";
  if (age >= 6 && age <= 11) return "Children (6-11)";
  if (age >= 12 && age <= 17) return "Adolescents (12-17)";
  if (age >= 18 && age <= 29) return "Young Adult (18-29)";
  if (age >= 30 && age <= 64) return "Adult (30-64)";
  if (age >= 65) return "Older Adult (65+)";
  return null;
};

/**
 * Validates a date of birth is a valid calendar date with minimum year of 1900
 * and is before today.
 *
 * Checks for:
 * - Valid calendar date (e.g., rejects February 30, September 31)
 * - Year >= 1900
 * - Date is strictly before today
 *
 * Supported inputs:
 * - `Date` objects
 * - Date strings in `YYYY-MM-DD` or `MM/DD/YYYY` format
 *
 * Other string formats are not guaranteed to be validated consistently and may
 * rely on environment-dependent `Date` parsing behavior.
 *
 * @param value - A `Date` object or a date string in `YYYY-MM-DD` or `MM/DD/YYYY` format
 * @returns true if date is valid; false otherwise
 *
 * @example
 * ```typescript
 * isValidDateOfBirth("1990-05-15")    // true
 * isValidDateOfBirth("02/30/1990")    // false (Feb 30 doesn't exist)
 * isValidDateOfBirth("1899-12-31")    // false (before 1900)
 * isValidDateOfBirth("2025-05-15")    // false (future date)
 * ```
 */
export const isValidDateOfBirth = (value: string | Date | null | undefined): boolean => {
  if (!value) {
    return false;
  }

  // Parse the date respecting calendar validity
  let parsedDate: Date | undefined;

  if (value instanceof Date) {
    // For Date objects, validate the calendar date by extracting components and re-validating
    const year = value.getFullYear();
    const month = value.getMonth(); // 0-indexed
    const day = value.getDate();
    parsedDate = createValidatedLocalDate(year, month, day);
    // This ensures we validate the actual calendar date displayed to the user
  } else {
    parsedDate = parseDateForCalendarComparison(value);
  }

  if (!parsedDate) {
    return false;
  }

  // Check minimum year is 1900
  if (parsedDate.getFullYear() < 1900) {
    return false;
  }

  // Check date is before today
  return isDateBeforeToday(parsedDate);
};

/**
 * Returns the earliest schedulable datetime based on the user's role:
 * - patient navigator: 1 calendar day from now
 * - all other roles: 2 business days from now, skipping weekends
 *
 * Uses the ZIP-code timezone when `userTimezone` is provided.
 */
export const getMinimumAppointmentMoment = (
  currentDate: Date = new Date(),
  userTimezone?: string,
  userRole?: UserRole,
): moment.Moment => {
  const workingMoment = userTimezone ? moment.tz(currentDate, userTimezone) : moment(currentDate);
  if (userRole === "patient navigator") {
    return workingMoment.add(1, "day");
  }

  const earliestAppointmentMoment = workingMoment.clone().startOf("day");
  let businessDaysAdded = 0;

  while (businessDaysAdded < 2) {
    earliestAppointmentMoment.add(1, "day");
    const dayOfWeek = earliestAppointmentMoment.day();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    if (!isWeekend) {
      businessDaysAdded++;
    }
  }

  return earliestAppointmentMoment;
};
